import * as React from 'react';
import { noop, never } from '../utils';
import { FocusedInput } from './useDatePicker';

interface DatePickerContextValue {
  isDateSelected: (date: Date) => boolean;
  isDateHovered: (date: Date) => boolean;
  isFirstOrLastSelectedDate: (date: Date) => boolean;
  isDateBlocked: (date: Date) => boolean;
  isDateFocused: (date: Date) => boolean;
  dateFormat: string;
  selectPhase: FocusedInput;
  focusedDate: Date | null;
  onDateHover: (date: Date | null) => void;
  onDateSelect: (date: Date) => void;
  onDateFocus: (date: Date) => void;
  isStartDate: (date: Date) => boolean;
  isEndDate: (date: Date) => boolean;
  getMinimumEnabledDate: (date: Date, diff?: number) => Date;
}

export const datePickerContextDefaultValue: DatePickerContextValue = {
  dateFormat: 'MM/DD/YYYY',
  focusedDate: null,
  selectPhase: null,
  isDateFocused: never,
  isDateSelected: never,
  isDateHovered: never,
  isDateBlocked: never,
  isFirstOrLastSelectedDate: never,
  onDateFocus: noop,
  onDateHover: noop,
  onDateSelect: noop,
  isStartDate: never,
  isEndDate: never,
  getMinimumEnabledDate: () => new Date()
};

export const DatePickerContext = React.createContext<DatePickerContextValue>(
  datePickerContextDefaultValue
);

interface DatePickerProviderProps extends DatePickerContextValue {
  children: React.ReactNode;
}

// Optimized provider that memoizes the context value to prevent unnecessary re-renders
export const DatePickerProvider: React.FC<DatePickerProviderProps> = ({ children, ...props }) => {
  // Memoize the context value to prevent re-renders when reference equality breaks
  const memoizedValue: DatePickerContextValue = React.useMemo(
    () => ({
      dateFormat: props.dateFormat,
      focusedDate: props.focusedDate,
      selectPhase: props.selectPhase,
      // Memoize function references to prevent child re-renders
      isDateSelected: props.isDateSelected,
      isDateHovered: props.isDateHovered,
      isFirstOrLastSelectedDate: props.isFirstOrLastSelectedDate,
      isDateBlocked: props.isDateBlocked,
      isDateFocused: props.isDateFocused,
      onDateHover: props.onDateHover,
      onDateSelect: props.onDateSelect,
      onDateFocus: props.onDateFocus,
      isStartDate: props.isStartDate,
      isEndDate: props.isEndDate,
      getMinimumEnabledDate: props.getMinimumEnabledDate
    }),
    [
      props.dateFormat,
      props.focusedDate,
      props.selectPhase,
      props.isDateSelected,
      props.isDateHovered,
      props.isFirstOrLastSelectedDate,
      props.isDateBlocked,
      props.isDateFocused,
      props.onDateHover,
      props.onDateSelect,
      props.onDateFocus,
      props.isStartDate,
      props.isEndDate,
      props.getMinimumEnabledDate
    ]
  );

  return <DatePickerContext.Provider value={memoizedValue}>{children}</DatePickerContext.Provider>;
};
